import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Text, Group, Stack } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay, 
  faPause, 
  faVolumeHigh, 
  faTimes,
  faForward,
  faMusic
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { createAuthenticatedAudio } from '../../utils/audioProxyUtil';
import styles from './AudioSelectionModal.module.css';
import { AudioTrack } from './types';

interface AudioSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  finalAudioUrl?: string | null;
  originalAudioUrl?: string | null;
  projectTitle: string;
  tracks?: AudioTrack[];
  onGenerateAudio?: (selectedAudioUrl?: string) => void; // Modified to accept selected audio URL
  onAudioUpdated?: (newFinalAudioUrl: string) => void; // Callback when finalAudio is updated
}

interface AudioOption {
  id: string;
  label: string;
  description: string;
  url?: string;
  value: string;
}

const AudioSelectionModal: React.FC<AudioSelectionModalProps> = ({
  isOpen,
  onClose,
  projectId,
  finalAudioUrl,
  originalAudioUrl,
  projectTitle,
  tracks = [],
  onGenerateAudio,
  onAudioUpdated
}) => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [audioElements, setAudioElements] = useState<Map<string, HTMLAudioElement>>(new Map());
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Create audio options based on available audio
  const audioOptions: AudioOption[] = [
    ...(finalAudioUrl ? [{
      id: 'final',
      label: 'Final Mixed Audio',
      description: 'Use the mixed and mastered audio from your project',
      url: finalAudioUrl,
      value: finalAudioUrl
    }] : []),
    ...(originalAudioUrl ? [{
      id: 'original',
      label: 'Original Audio',
      description: 'Use the original audio uploaded to your project',
      url: originalAudioUrl,
      value: originalAudioUrl
    }] : [])
  ];

  // Check if we have any audio at all - including tracks for generation
  const hasTracksButNoMixedAudio = tracks && tracks.length > 0 && tracks.some(track => track.url) && !finalAudioUrl;
  const hasAnyAudio = Boolean(
    (finalAudioUrl && finalAudioUrl.trim() !== '') || 
    (originalAudioUrl && originalAudioUrl.trim() !== '') ||
    hasTracksButNoMixedAudio
  );

  // Debug logging
  console.log('AudioSelectionModal Debug:', {
    finalAudioUrl,
    originalAudioUrl,
    tracksCount: tracks?.length || 0,
    tracksWithAudio: tracks?.filter(track => track.url).length || 0,
    hasTracksButNoMixedAudio,
    hasAnyAudio,
    audioOptionsLength: audioOptions.length
  });

  // If no final audio but we have tracks, offer to generate
  if (hasTracksButNoMixedAudio) {
    audioOptions.unshift({
      id: 'generate',
      label: 'Generate Mixed Audio',
      description: 'Mix your audio tracks first, then continue to caption creation',
      value: 'generate'
    });
  } else if (!hasAnyAudio) {
    // If no audio exists at all, show upload message
    audioOptions.push({
      id: 'upload',
      label: 'Upload Audio Required',
      description: 'You need to upload or generate audio before proceeding to content creation. Please add audio tracks to your project first.',
      value: 'upload'
    });
  }

  // Initialize audio elements - refresh when audio sources change
  useEffect(() => {
    const initializeAudioElements = async () => {
      const newAudioElements = new Map<string, HTMLAudioElement>();
      
      for (const option of audioOptions) {
        if (option.url && option.url.trim() !== '' && option.url !== 'upload' && option.url !== 'generate') {
          try {
            console.log(`🎵 Creating authenticated audio element for ${option.label}:`, option.url);
            
            // Use the authenticated audio proxy utility
            const audio = await createAuthenticatedAudio(option.url);
            audio.preload = 'none'; // Don't preload to avoid unnecessary requests
            audio.addEventListener('ended', () => setPlayingAudio(null));
            audio.addEventListener('error', (e) => {
              console.warn(`Audio load error for ${option.label}:`, e);
              // Don't show notification for audio load errors - they're expected sometimes
            });
            
            newAudioElements.set(option.id, audio);
            console.log(`✅ Successfully created audio element for ${option.label}`);
          } catch (error) {
            console.warn(`❌ Failed to create authenticated audio element for ${option.label}:`, error);
          }
        }
      }

      setAudioElements(newAudioElements);
    };

    initializeAudioElements();

    // Cleanup function
    return () => {
      audioElements.forEach(audio => {
        audio.pause();
        audio.src = '';
        // Clean up blob URLs to prevent memory leaks
        if (audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      });
    };
  }, [finalAudioUrl, originalAudioUrl, tracks]); // Added tracks back to dependencies

  // Handle audio play/pause
  const toggleAudioPlayback = async (optionId: string) => {
    const audio = audioElements.get(optionId);
    const option = audioOptions.find(opt => opt.id === optionId);
    
    if (!audio || !option?.url) return;

    try {
      if (playingAudio === optionId) {
        // Pause current audio
        audio.pause();
        setPlayingAudio(null);
      } else {
        // Stop any currently playing audio
        if (playingAudio) {
          const currentAudio = audioElements.get(playingAudio);
          if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
          }
        }

        // Set src only when we want to play (lazy loading)
        if (!audio.src || audio.src === '') {
          console.log(`🎵 Setting audio src for playback: ${option.label}`);
          // The audio element should already have the correct src from createAuthenticatedAudio
          // If not, we need to recreate it
          try {
            const newAudio = await createAuthenticatedAudio(option.url);
            newAudio.preload = 'none';
            newAudio.addEventListener('ended', () => setPlayingAudio(null));
            newAudio.addEventListener('error', (e) => {
              console.warn(`Audio load error for ${option.label}:`, e);
            });
            
            // Replace the old audio element
            audioElements.set(optionId, newAudio);
            await newAudio.play();
          } catch (error) {
            console.error('Failed to create/play authenticated audio:', error);
            throw error;
          }
        } else {
          // Play the existing audio
          await audio.play();
        }
        setPlayingAudio(optionId);
      }
    } catch (error) {
      console.warn('Audio playback error:', error);
      // Only show notification for actual playback errors, not loading errors
      if (error.name !== 'NotSupportedError') {
        showNotification({
          title: 'Playback Error',
          message: 'Failed to play audio. Please try again.',
          type: 'error'
        });
      }
    }
  };

  // Handle navigation to caption creation
  const handleContinue = () => {
    if (!selectedOption && audioOptions.length > 1) {
      showNotification({
        title: 'Selection Required',
        message: 'Please select an audio option to continue',
        type: 'error'
      });
      return;
    }

    // Stop any playing audio
    if (playingAudio) {
      const audio = audioElements.get(playingAudio);
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }

    // Handle special case for generate audio
    const selectedAudioOption = audioOptions.find(opt => opt.id === selectedOption);
    
    if (selectedAudioOption?.value === 'generate') {
      // Trigger audio generation instead of going to content creation
      if (onGenerateAudio) {
        // showNotification({
        //   title: 'Generating Audio',
        //   message: 'Starting audio generation process...',
        //   type: 'info'
        // });
        onClose(); // Close modal first
        onGenerateAudio(); // Trigger the audio generation (Mix Audio functionality)
        return;
      } else {
        // Fallback if no callback provided
        showNotification({
          title: 'Generate Audio First',
          message: 'Please generate or upload audio before proceeding to caption creation',
          type: 'info'
        });
        onClose();
        return;
      }
    }

    // Get the selected audio URL
    const selectedAudioUrl = selectedAudioOption?.url || '';
    
    // Check if user selected different audio than current finalAudio
    const needsToUpdateFinalAudio = selectedAudioUrl && 
                                   selectedAudioUrl !== finalAudioUrl && 
                                   selectedAudioUrl.trim() !== '';
    
    if (needsToUpdateFinalAudio && onGenerateAudio) {
      // Use existing mix functionality with selected audio
      // showNotification({
      //   title: 'Processing Audio',
      //   message: `Updating to ${selectedAudioOption?.label}...`,
      //   type: 'info'
      // });
      onClose(); // Close modal first
      onGenerateAudio(selectedAudioUrl); // Pass selected audio to existing mix function
      return;
    }

    // Navigate to content creation with selected audio (when no update needed)
    const contentCreationUrl = selectedAudioUrl 
      ? `/caption?project-id=${projectId}&audio-url=${encodeURIComponent(selectedAudioUrl)}`
      : `/caption?project-id=${projectId}`;

    // showNotification({
    //   title: 'Navigating to Content Creation',
    //   message: `Using ${selectedAudioOption?.label || 'no audio'} for content generation`,
    //   type: 'success'
    // });

    navigate(contentCreationUrl);
    onClose();
  };

  // Auto-select if only one option available
  useEffect(() => {
    if (audioOptions.length === 1) {
      setSelectedOption(audioOptions[0].id);
    }
  }, [audioOptions.length]);

  // Reset selection when audio sources change to refresh the modal
  useEffect(() => {
    console.log('🔄 Audio sources changed, refreshing modal options');
    if (audioOptions.length > 1) {
      // Reset selection to force user to choose again with new options
      setSelectedOption('');
    } else if (audioOptions.length === 1) {
      // Auto-select the only option
      setSelectedOption(audioOptions[0].id);
    }
  }, [finalAudioUrl, originalAudioUrl, tracks]); // Reset when audio sources change

  // If modal should be open but Mantine modal has issues, use custom modal
  if (isOpen) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '0',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          {/* Modal Header */}
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            padding: '20px 24px',
            borderRadius: '16px 16px 0 0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: 0,
            }}>
              <FontAwesomeIcon icon={faMusic} style={{ fontSize: '20px', color: 'white' }} />
              <h2 style={{ color: 'white', fontWeight: 600, fontSize: '18px', margin: 0 }}>
                Select Audio for Caption Creation
              </h2>
            </div>
          </div>

          {/* Modal Body */}
          <div style={{ padding: '24px' }}>
            <Stack gap="lg">
              <Text size="sm" style={{ color: '#a0a0a0' }}>
                Choose which audio to use for caption generation in <strong style={{ color: 'white' }}>{projectTitle}</strong>
              </Text>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {audioOptions.map((option) => (
                  <div
                    key={option.id}
                    style={{
                      background: selectedOption === option.id 
                        ? 'rgba(59, 130, 246, 0.1)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      border: selectedOption === option.id 
                        ? '2px solid #3b82f6' 
                        : '2px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: selectedOption === option.id 
                        ? '0 0 20px rgba(59, 130, 246, 0.2)' 
                        : 'none',
                    }}
                    onClick={() => setSelectedOption(option.id)}
                    onMouseEnter={(e) => {
                      if (selectedOption !== option.id) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOption !== option.id) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="audioOption"
                        value={option.id}
                        checked={selectedOption === option.id}
                        onChange={() => setSelectedOption(option.id)}
                        style={{ width: '20px', height: '20px', accentColor: '#3b82f6' }}
                      />
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'white' }}>
                          {option.label}
                        </h3>
                        <p style={{ margin: 0, fontSize: '14px', color: '#a0a0a0', lineHeight: 1.4 }}>
                          {option.description}
                        </p>
                      </div>
                      {option.url && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAudioPlayback(option.id);
                          }}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backdropFilter: 'blur(10px)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <FontAwesomeIcon 
                            icon={playingAudio === option.id ? faPause : faPlay} 
                          />
                        </div>
                      )}
                      {option.id === 'generate' && (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'not-allowed',
                            opacity: 0.6,
                          }}
                        >
                          <FontAwesomeIcon icon={faMusic} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: hasAnyAudio ? '16px' : '0',
                marginTop: '16px' 
              }}>
                {hasAnyAudio && (
                  <div
                    onClick={onClose}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      backdropFilter: 'blur(10px)',
                      cursor: 'pointer',
                      display: 'inline-block',
                      fontSize: '14px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Cancel
                  </div>
                )}
                
                {hasAnyAudio ? (
                  <div
                    onClick={selectedOption ? handleContinue : undefined}
                    style={{
                      background: selectedOption 
                        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
                        : 'rgba(99, 102, 241, 0.3)',
                      border: 'none',
                      color: 'white',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: selectedOption 
                        ? '0 4px 14px 0 rgba(99, 102, 241, 0.39)' 
                        : 'none',
                      cursor: selectedOption ? 'pointer' : 'not-allowed',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      opacity: selectedOption ? 1 : 0.5,
                    }}
                    onMouseEnter={(e) => {
                      if (selectedOption) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px 0 rgba(99, 102, 241, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOption) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(99, 102, 241, 0.39)';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faForward} />
                    {selectedOption === 'generate' ? 'Continue' : 'Continue to Content Creation'}
                  </div>
                ) : (
                  <div
                    onClick={onClose}
                    style={{
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      border: 'none',
                      color: 'white',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px 0 rgba(99, 102, 241, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(99, 102, 241, 0.39)';
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Close
                  </div>
                )}
              </div>
            </Stack>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AudioSelectionModal;
